// src/lib/metrics/primitives.ts
/**
 * Statistical primitives - foundational functions for all metric calculations
 * 
 * All functions are pure, accept arrays, and handle edge cases consistently:
 * - Empty arrays return null
 * - Invalid values (NaN, Infinity) are filtered out
 * - Division by zero returns null
 */

import type { Series, ValidSeries } from './types';

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Filter series to only valid finite numbers
 */
export function filterValid(values: Series): ValidSeries {
  return values.filter(
    (v): v is number => v !== null && v !== undefined && Number.isFinite(v)
  );
}

/**
 * Safely clamp a value to finite number with fallback
 */
export function clampFinite(value: unknown, fallback: number = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

// ============================================================================
// CENTRAL TENDENCY
// ============================================================================

/**
 * Calculate arithmetic mean
 * @returns null if no valid values
 */
export function mean(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length === 0) return null;
  
  const sum = valid.reduce((acc, v) => acc + v, 0);
  return sum / valid.length;
}

/**
 * Calculate median (50th percentile)
 * @returns null if no valid values
 */
export function median(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length === 0) return null;
  
  const sorted = [...valid].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1]! + sorted[mid]!) / 2;
  }
  return sorted[mid]!;
}

/**
 * Calculate geometric mean (useful for returns)
 * @returns null if no valid values or any non-positive values
 */
export function geometricMean(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length === 0) return null;
  if (valid.some(v => v <= 0)) return null;
  
  const logSum = valid.reduce((acc, v) => acc + Math.log(v), 0);
  return Math.exp(logSum / valid.length);
}

// ============================================================================
// DISPERSION
// ============================================================================

/**
 * Calculate variance (sample variance with Bessel's correction)
 * @param population - if true, use population variance (N divisor)
 * @returns null if insufficient data
 */
export function variance(values: Series, population: boolean = false): number | null {
  const valid = filterValid(values);
  const n = valid.length;
  
  if (n < 2) return null;
  
  const m = mean(valid);
  if (m === null) return null;
  
  const sumSquaredDiff = valid.reduce((acc, v) => acc + Math.pow(v - m, 2), 0);
  const divisor = population ? n : n - 1;
  
  return sumSquaredDiff / divisor;
}

/**
 * Calculate standard deviation
 * @param population - if true, use population std dev (N divisor)
 * @returns null if insufficient data
 */
export function stdDev(values: Series, population: boolean = false): number | null {
  const v = variance(values, population);
  if (v === null) return null;
  return Math.sqrt(v);
}

/**
 * Calculate downside deviation (semi-deviation below threshold)
 * Used for Sortino ratio calculation
 * @param threshold - typically 0 or risk-free rate
 * @param useSampleVariance - if true, use N-1 divisor (matches SQL backend)
 */
export function downsideDeviation(
  values: Series,
  threshold: number = 0,
  useSampleVariance: boolean = true
): number | null {
  const valid = filterValid(values);
  if (valid.length < 2) return null;
  
  const downside = valid.filter(v => v < threshold);
  if (downside.length === 0) return null;
  
  // Sum of squared values below threshold
  // Using r² directly (same as (r - 0)² when threshold = 0)
  const sumSquared = downside.reduce(
    (acc, v) => acc + v * v,
    0
  );
  
  // Divide by N-1 (sample) or N (population) of the full window
  const divisor = useSampleVariance ? valid.length - 1 : valid.length;
  if (divisor <= 0) return null;
  
  return Math.sqrt(sumSquared / divisor);
}

/**
 * Calculate upside deviation (semi-deviation above threshold)
 * @param threshold - typically 0 or risk-free rate
 */
export function upsideDeviation(
  values: Series,
  threshold: number = 0
): number | null {
  const valid = filterValid(values);
  if (valid.length < 2) return null;
  
  const upside = valid.filter(v => v > threshold);
  if (upside.length === 0) return null;
  
  const sumSquaredUpside = upside.reduce(
    (acc, v) => acc + Math.pow(v - threshold, 2),
    0
  );
  
  return Math.sqrt(sumSquaredUpside / valid.length);
}

// ============================================================================
// COVARIANCE & CORRELATION
// ============================================================================

/**
 * Calculate covariance between two series
 * @param population - if true, use population covariance (N divisor)
 * @returns null if insufficient data
 */
export function covariance(
  x: Series,
  y: Series,
  population: boolean = false
): number | null {
  const xValid = filterValid(x);
  const yValid = filterValid(y);
  
  const n = Math.min(xValid.length, yValid.length);
  if (n < 2) return null;
  
  // Align to shorter length (from end)
  const xAligned = xValid.slice(-n);
  const yAligned = yValid.slice(-n);
  
  const xMean = mean(xAligned);
  const yMean = mean(yAligned);
  if (xMean === null || yMean === null) return null;
  
  let sumProduct = 0;
  for (let i = 0; i < n; i++) {
    sumProduct += (xAligned[i]! - xMean) * (yAligned[i]! - yMean);
  }
  
  const divisor = population ? n : n - 1;
  return sumProduct / divisor;
}

/**
 * Calculate Pearson correlation coefficient
 * @returns value between -1 and 1, or null if insufficient data
 */
export function correlation(x: Series, y: Series): number | null {
  const xValid = filterValid(x);
  const yValid = filterValid(y);
  
  const n = Math.min(xValid.length, yValid.length);
  if (n < 2) return null;
  
  const xAligned = xValid.slice(-n);
  const yAligned = yValid.slice(-n);
  
  const xMean = mean(xAligned);
  const yMean = mean(yAligned);
  if (xMean === null || yMean === null) return null;
  
  let sumXY = 0;
  let sumX2 = 0;
  let sumY2 = 0;
  
  for (let i = 0; i < n; i++) {
    const dx = xAligned[i]! - xMean;
    const dy = yAligned[i]! - yMean;
    sumXY += dx * dy;
    sumX2 += dx * dx;
    sumY2 += dy * dy;
  }
  
  const denominator = Math.sqrt(sumX2 * sumY2);
  if (denominator === 0) return null;
  
  const corr = sumXY / denominator;
  return Math.max(-1, Math.min(1, corr)); // Clamp to [-1, 1]
}

/**
 * Calculate correlation matrix for multiple series
 */
export function correlationMatrix(seriesList: Series[]): (number | null)[][] {
  const n = seriesList.length;
  const matrix: (number | null)[][] = Array.from({ length: n }, () =>
    Array(n).fill(null)
  );
  
  for (let i = 0; i < n; i++) {
    matrix[i]![i] = 1; // Diagonal is always 1
    
    for (let j = i + 1; j < n; j++) {
      const corr = correlation(seriesList[i]!, seriesList[j]!);
      matrix[i]![j] = corr;
      matrix[j]![i] = corr; // Symmetric
    }
  }
  
  return matrix;
}

// ============================================================================
// AGGREGATION
// ============================================================================

/**
 * Calculate sum of valid values
 */
export function sum(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length === 0) return null;
  return valid.reduce((acc, v) => acc + v, 0);
}

/**
 * Calculate min of valid values
 */
export function min(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length === 0) return null;
  return Math.min(...valid);
}

/**
 * Calculate max of valid values
 */
export function max(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length === 0) return null;
  return Math.max(...valid);
}

/**
 * Calculate range (max - min)
 */
export function range(values: Series): number | null {
  const minVal = min(values);
  const maxVal = max(values);
  if (minVal === null || maxVal === null) return null;
  return maxVal - minVal;
}

/**
 * Calculate percentage of positive values
 * @returns percentage (0-100), or null if no valid values
 */
export function percentPositive(values: Series): number | null {
  const valid = filterValid(values);
  if (valid.length === 0) return null;
  const positive = valid.filter(v => v > 0).length;
  return (positive / valid.length) * 100;
}

